import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { ReactComponent as Close } from "../../assets/icons/x.svg";
import { Check, Loader, Mail } from "react-feather";
import ForgotPasswordForm from "./ForgotPasswordForm";
import VerificationCode from "./VerificationCode";
import EmailCodeSignIn from "./EmailCodeSignIn";
import { signInCodeEnabled } from "../../utility/signInCode";
import ResetPassword from "./ResetPassword";
import PasswordChanged from "./PasswordChanged";
import { getQueryParam, useScreenTransition } from "../../Hooks/useScreenTransition";
import { AuthContext } from "../../Context/AuthContext";
import { NotificationContext } from "../../Context/NotificationContext";
import "./SignInForm.css";
import { FormTextInput } from "../../Components/FormTextInput";
import { auth, firestore } from "../../firebase.config";
import { useLocation } from "react-router-dom";
import { announceJoin, joinResultPath, joinTeamWithLink } from "../../utility/teamInviteLinks";
import { goToLanding, landingAfterSignIn, returnPathFrom } from "../../utility/landing";
import { useCheckoutT } from "../../i18n/checkout";

// What Firebase answers for a wrong password (with email enumeration
// protection on, also for an email that has no account).
const WRONG_PASSWORD = ["auth/wrong-password", "auth/invalid-credential", "auth/invalid-login-credentials", "auth/user-not-found"];

export default function SignInForm({
  open,
  isLoginFormOpen,
  isCreateAccountOpen,
  pageRedirect = true,
  showDirectForgotPassword = false,
  // The email to start with (the checkout's, when it has an account).
  initialEmail = ""
}) {
  const modalRoot = document.getElementById("modal-root");

  const { signedIn, setSignedIn } = useContext(AuthContext);
  // English everywhere but the checkout (see i18n/checkout).
  const { t } = useCheckoutT();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [signInState, setSignInState] = useState("login");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [verifiedCode, setVerifiedCode] = useState(null);
  const { transitionTo} = useScreenTransition();

  const joinTeamParam= getQueryParam("join-team");
  const joinTeamToken= getQueryParam("token");
  const location = useLocation();

  const isLoginOpen = () => {
    isLoginFormOpen(false);
    setSignInState("login");
    setWrongPassword(false);
    setEmailError(false);
    setPasswordError(false);
    setShowPassError(false);
  };

  const isCreateAccount = () => {
    isLoginFormOpen(false);
    isCreateAccountOpen(true);
  };

  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // The button's text, by key (see i18n/checkout/strings/account.js).
  const [btnKey, setBtnKey] = useState("account.signIn.button");
  const [showPassError, setShowPassError] = useState(false);
  // The password was wrong: the window offers a code by email instead.
  const [wrongPassword, setWrongPassword] = useState(false);

  const { showNotification } = useContext(NotificationContext);


  useEffect(() => {
    if (signedIn) {
      isLoginOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  // Opened for an email (the checkout's): it is typed in already.
  useEffect(() => {
    if (open && initialEmail) {
      setEmail(initialEmail);
    }
  }, [open, initialEmail]);

  useEffect(()=> {
    if(showDirectForgotPassword){
      handleForgotPassword();
    }
  }, [showDirectForgotPassword])

  const handleEmailChange = (value) => {
    setEmailError(false);
    setEmail(value);
  };

  const handlePasswordChange = (value) => {
    setPasswordError(false);
    setWrongPassword(false);
    setPassword(value);
  };

  const handleForgotPassword = () => {
    setSignInState("forgot-password");
  };

  const handleVerification = () => {
    setSignInState("verification");
  };

  const handleResetPassword = (code) => {
    setVerifiedCode(code);
    setSignInState("reset-password");
  };

  const handlePasswordChanged = () => {
    setSignInState("password-changed");
  };

  const backToSignIn = () => {
    setSignInState("login");
  };

  // After signing in, with a password or a code by email.
  const afterSignIn = async (credential) => {
    // Signing in from a team invite link joins that team (on the server).
    let joinPath = null;
    if(joinTeamParam && joinTeamToken){
      const status = await joinTeamWithLink(joinTeamToken).catch(() => null);
      joinPath = joinResultPath(status);
      announceJoin(status, showNotification);
    }
    // Clients start on their orders, the office and translators in the
    // portal; someone sent home from a private page goes back to it.
    // (Pages that keep the person where they are pass pageRedirect=false.)
    if (pageRedirect) {
      const path = joinPath || returnPathFrom(location) || await landingAfterSignIn(credential.user.uid);
      goToLanding(transitionTo, path);
    }
    setSignedIn(true);
  };

  const signIn = () => {
    if (email === "" || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
    }

    if (password === "" || password.length < 6) {
      setPasswordError(true);
    }

    if (
      email === "" ||
      !/\S+@\S+\.\S+/.test(email) ||
      password === "" ||
      password.length < 6
    ) {
      return;
    }


    setBtnKey("account.signIn.signing");
    setIsLoading(true);
    auth.signInWithEmailAndPassword(email.trim().toLowerCase(), password)
      .then(async (credential) => {
        await afterSignIn(credential);
        setBtnKey("account.signIn.button");
        setIsLoading(false);
      })
      .catch((e) => {
        // console.log(e);
        setBtnKey("account.signIn.button");
        setIsLoading(false);
        // A wrong password (or an email without one): the message under the
        // password offers a code by email.
        if (signInCodeEnabled && e && WRONG_PASSWORD.includes(e.code)) {
          setWrongPassword(true);
          return;
        }
        showNotification(
          t("notify.error"),
          // A team member their team deactivated (their sign-in is off).
          t(e && e.code === "auth/user-disabled" ? "account.signIn.disabled" : "account.signIn.error"),
          "error"
        );
      });
  };

  const triggerSignIn = () => {
    if (!(email === "" || password === "")) {
      signIn();
    }
  };

  if (!open) {
    return <></>;
  }

  // Leaving a field empty is not an error yet (only signing in with it is):
  // a tap on the field and away used to turn it red.
  const handleEmailBlur = () => {
    if (email !== "" && !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
    }
  };

  const handlePasswordBlur = () => {
    if (password === "") {
      return;
    }
    if (password.length < 6) {
      setPasswordError(true);
    }
    setShowPassError(true);
  };
  return ReactDOM.createPortal(
    <div className="modal">
      <div className="modal-content">
        {signInState === "login" ? (
          <div className="signInForm">
            <div className="signInFormHeader">
              <h2 className="signInFormTitle">{t("account.signIn.title")}</h2>
              <Close className="signInFormIcon" onClick={isLoginOpen} />
            </div>

            <div className="signInFormContainer">
              <FormTextInput
                email={true}
                incorrect={emailError}
                placeholder={t("field.email")}
                onBlur={handleEmailBlur}
                clearInputIcon={true}
                value={email}
                onChange={handleEmailChange}
                onKeyDown={(e) => e.key === "Enter" && triggerSignIn()}
              />
            </div>

            {emailError ? (
              <span className="signInFormInputValidation">
                {t("account.invalidEmailFormat")}
              </span>
            ) : null}

            <div className="signInFormContainer">
              <FormTextInput
                showPasswordIcon={true}
                password={true}
                incorrect={passwordError}
                onBlur={handlePasswordBlur}
                placeholder={t("account.password")}
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={(e) => e.key === "Enter" && triggerSignIn()}
              />
            </div>

            {showPassError && (
              <span
                className={`passwordValidation ${
                  passwordError
                    ? "passwordError"
                    : password.length >= 6
                    ? "passwordSuccess"
                    : "passwordDefault"
                }`}
              >
                <Check className="passwordCheck" />
                {t("account.passwordRule")}
              </span>
            )}

            {wrongPassword && (
              <div className="signInWrongPassword" role="alert" data-testid="wrong-password">
                <span>{t("account.code.wrongPassword")}</span>
                <button type="button" className="signInCodeLink" data-action="code-after-wrong-password" onClick={() => setSignInState("code")}>
                  {t("account.code.offer")}
                </button>
              </div>
            )}

            <span
              className="signInFormForgotPassword"
              onClick={handleForgotPassword}
            >
              {t("account.signIn.forgot")}
            </span>
            <button
              className="signInFormButton"
              disabled={
                email === "" ||
                password === "" ||
                isLoading ||
                emailError ||
                passwordError
              }
              onClick={() => signIn()}
            >
              {t(btnKey)}
              {isLoading && <Loader className="btnLoader" />}
            </button>

            {signInCodeEnabled && (
              <>
                <div className="signInOr" aria-hidden="true"><span>{t("account.code.or")}</span></div>
                <button type="button" className="signInFormButton signInFormButtonOut" data-action="email-code" onClick={() => setSignInState("code")}>
                  <Mail className="signInFormButtonIcon" aria-hidden="true" />
                  {t("account.code.button")}
                </button>
              </>
            )}

            <div className="signInFormDontHaveAccount">
              <span className="signInFormDontHaveAccountSpan">
                {t("account.signIn.noAccount")}
              </span>
              <span
                className="signInFormDontHaveAccountRegister"
                onClick={() => isCreateAccount()}
              >
                {t("account.signIn.register")}
              </span>
            </div>
          </div>
        ) : null}
        {signInState === "code" ? (
          <EmailCodeSignIn
            email={email}
            setEmail={handleEmailChange}
            onSignedIn={afterSignIn}
            onUsePassword={backToSignIn}
            onClose={isLoginOpen}
          />
        ) : null}
        {signInState === "forgot-password" ? (
          <ForgotPasswordForm
            isLoginOpen={isLoginOpen}
            goToVerification={handleVerification}
            backToSignIn={backToSignIn}
            email={forgotPasswordEmail}
            setEmail={setForgotPasswordEmail}
          />
        ) : null}

        {signInState === "verification" ? (
          <VerificationCode
            backToSignIn={backToSignIn}
            isLoginOpen={isLoginOpen}
            goToResetPassword={handleResetPassword}
            email={forgotPasswordEmail}
          />
        ) : null}

        {signInState === "reset-password" ? (
          <ResetPassword
            isLoginOpen={isLoginOpen}
            backToSignIn={backToSignIn}
            handlePasswordChanged={handlePasswordChanged}
            email={forgotPasswordEmail}
            code={verifiedCode}
          />
        ) : null}

        {signInState === "password-changed" ? (
          <PasswordChanged
            backToSignIn={backToSignIn}
            isLoginOpen={isLoginOpen}
          />
        ) : null}
      </div>
    </div>,
    modalRoot
  );
}
